import { Link } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';
import SEO from '../components/SEO';

const PrivacyPage = () => {
  return (
    <div className="min-h-screen pt-24 pb-16 bg-slate-950" data-testid="privacy-page">
      <SEO 
        title="Privacy Policy"
        description="Learn how Bit Buddies collects, uses, and protects your personal information."
        url="/privacy"
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/" className="inline-flex items-center text-slate-400 hover:text-white mb-8 transition-colors">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Home
        </Link>

        <div className="flex items-center gap-3 mb-8">
          <Shield className="h-8 w-8 text-cyan-400" />
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-white">Privacy Policy</h1>
        </div>

        <div className="prose prose-invert max-w-none">
          <p className="text-slate-400 mb-6">Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>

          <div className="space-y-8 text-slate-300">
            <section>
              <h2 className="font-heading text-xl font-semibold text-white mb-4">1. Introduction</h2>
              <p>Bit Buddies ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and use our services.</p>
            </section>

            <section>
              <h2 className="font-heading text-xl font-semibold text-white mb-4">2. Information We Collect</h2>
              <p>We may collect information about you in various ways, including:</p>
              <h3 className="font-semibold text-white mt-4 mb-2">Personal Data</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Name and contact information (email address, company name)</li>
                <li>Payment and billing information (processed by third-party providers)</li>
                <li>Communications you send to us</li>
              </ul>
              <h3 className="font-semibold text-white mt-4 mb-2">Automatically Collected Data</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>IP address and approximate geographic location</li>
                <li>Browser type and operating system</li>
                <li>Pages visited and time spent on pages</li>
                <li>Referring website addresses</li>
              </ul>
            </section>

            <section>
              <h2 className="font-heading text-xl font-semibold text-white mb-4">3. How We Use Your Information</h2>
              <p>We use the information we collect to:</p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li>Provide, operate, and maintain our services</li>
                <li>Process transactions and send related information</li>
                <li>Respond to your inquiries and provide customer support</li>
                <li>Send you marketing and promotional communications (with your consent)</li>
                <li>Analyze usage patterns and improve our services</li>
                <li>Protect against fraudulent or unauthorized activity</li>
              </ul>
            </section>

            <section>
              <h2 className="font-heading text-xl font-semibold text-white mb-4">4. Information Sharing</h2>
              <p>We may share your information in the following situations:</p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li><strong className="text-white">Service Providers:</strong> With third parties that perform services on our behalf (payment processing, analytics)</li>
                <li><strong className="text-white">Legal Requirements:</strong> If required by law or in response to valid requests by public authorities</li>
                <li><strong className="text-white">Business Transfers:</strong> In connection with any merger, sale, or acquisition of our business</li>
              </ul>
            </section>

            <section>
              <h2 className="font-heading text-xl font-semibold text-white mb-4">5. Data Security</h2>
              <p>We implement appropriate technical and organizational measures to protect your personal information. However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.</p>
            </section>

            <section>
              <h2 className="font-heading text-xl font-semibold text-white mb-4">6. Data Retention</h2>
              <p>We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law.</p>
            </section>

            <section>
              <h2 className="font-heading text-xl font-semibold text-white mb-4">7. Your Rights</h2>
              <p>Depending on your location, you may have the right to:</p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li>Access the personal information we hold about you</li>
                <li>Request correction of inaccurate data</li>
                <li>Request deletion of your data</li>
                <li>Object to or restrict certain processing</li>
                <li>Data portability</li>
              </ul>
              <p className="mt-4">To exercise these rights, please contact us at MDH@BitBuddies.online.</p>
            </section>

            <section>
              <h2 className="font-heading text-xl font-semibold text-white mb-4">8. Cookies and Tracking</h2>
              <p>We use cookies and similar tracking technologies to track activity on our Service and hold certain information. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent.</p>
            </section>

            <section>
              <h2 className="font-heading text-xl font-semibold text-white mb-4">9. Third-Party Links</h2>
              <p>Our Service may contain links to third-party websites. We are not responsible for the privacy practices or content of these third-party sites.</p>
            </section>

            <section>
              <h2 className="font-heading text-xl font-semibold text-white mb-4">10. Children's Privacy</h2>
              <p>Our Service is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13.</p>
            </section>

            <section>
              <h2 className="font-heading text-xl font-semibold text-white mb-4">11. Changes to This Policy</h2>
              <p>We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date.</p>
            </section>

            <section>
              <h2 className="font-heading text-xl font-semibold text-white mb-4">12. Contact Us</h2>
              <p>If you have questions about this Privacy Policy, please contact us at:</p>
              <p className="mt-2">
                <strong className="text-white">Email:</strong> MDH@BitBuddies.online<br />
                <strong className="text-white">Location:</strong> Addison, TX
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;
